import React from "react";

/* Hand-drawn SVG illustrations representing each free-activity tool.
   - Strokes use currentColor so they pick up the menu's brand colour.
   - Filled "paper" surfaces are off-white so they read as objects, not
     icons.
   - Sized via parent (width: 100%; height: 100%) - the SVGs themselves
     ship with a 64x64 viewBox so they remain crisp at any size.

   Each illustration is intentionally simple and representational so
   that at the 48px-ish menu thumbnail size you can still tell what
   it is in a glance. */

const STROKE_W = 2.5;
const PAPER = "#F6F4ED"; // warm off-white that pops on white card

const Wrap = ({ children, ...rest }) => (
  <svg
    viewBox="0 0 64 64"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    {...rest}
  >
    {children}
  </svg>
);

/* Vocab sheet creator - a stacked vocab sheet with bullet lines. */
export const IllusVocab = (props) => (
  <Wrap {...props}>
    {/* back sheet */}
    <rect x="11" y="14" width="34" height="42" rx="3.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* front sheet */}
    <rect x="18" y="8" width="34" height="42" rx="3.5" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    {/* header underline */}
    <line x1="24" y1="16" x2="40" y2="16" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* bullet rows */}
    <circle cx="24" cy="24" r="1.6" fill="currentColor" />
    <line x1="28" y1="24" x2="46" y2="24" stroke="currentColor" strokeWidth={STROKE_W * 0.85} strokeLinecap="round" />
    <circle cx="24" cy="31" r="1.6" fill="currentColor" />
    <line x1="28" y1="31" x2="44" y2="31" stroke="currentColor" strokeWidth={STROKE_W * 0.85} strokeLinecap="round" />
    <circle cx="24" cy="38" r="1.6" fill="currentColor" />
    <line x1="28" y1="38" x2="42" y2="38" stroke="currentColor" strokeWidth={STROKE_W * 0.85} strokeLinecap="round" />
  </Wrap>
);

/* Word/Name Printer - a printer with paper sliding out. */
export const IllusPrinter = (props) => (
  <Wrap {...props}>
    {/* paper coming out top */}
    <rect x="20" y="6" width="24" height="14" rx="1" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <line x1="24" y1="11" x2="40" y2="11" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
    <line x1="24" y1="15" x2="36" y2="15" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
    {/* printer body */}
    <path d="M12 22 H52 a2 2 0 0 1 2 2 V42 a2 2 0 0 1 -2 2 H44 V38 H20 V44 H12 a2 2 0 0 1 -2 -2 V24 a2 2 0 0 1 2 -2 Z" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    {/* printer indicator light */}
    <circle cx="48" cy="29" r="1.6" fill="currentColor" />
    {/* paper sliding out at bottom */}
    <rect x="20" y="38" width="24" height="18" rx="1" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <line x1="24" y1="45" x2="40" y2="45" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
    <line x1="24" y1="50" x2="38" y2="50" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
  </Wrap>
);

/* Word search maker - a grid of letters with the found word highlighted
   on the diagonal. Kept deliberately bold and sparse (3x3, no magnifier)
   so it still reads as a word-search grid at ~20px in the menu. */
export const IllusWordSearch = (props) => (
  <Wrap {...props}>
    <rect x="10" y="10" width="44" height="44" rx="5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* found-word highlight capsule down the diagonal, behind the letters */}
    <line x1="21" y1="21" x2="43" y2="43" stroke="currentColor" strokeWidth={STROKE_W * 2.8} strokeLinecap="round" opacity="0.28" />
    {/* 3x3 grid of letters; the three on the diagonal are the found word */}
    {[21, 32, 43].map((y, ry) =>
      [21, 32, 43].map((x, rx) => (
        <circle key={`${rx}-${ry}`} cx={x} cy={y} r="2.4" fill="currentColor" opacity={rx === ry ? 1 : 0.45} />
      ))
    )}
  </Wrap>
);

/* Free printables hub - stack of sheets / folder. */
export const IllusHub = (props) => (
  <Wrap {...props}>
    {/* back sheet */}
    <rect x="12" y="10" width="34" height="42" rx="3" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* mid sheet */}
    <rect x="17" y="14" width="34" height="42" rx="3" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    {/* front sheet with download arrow */}
    <rect x="22" y="18" width="34" height="42" rx="3" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    {/* download arrow */}
    <line x1="39" y1="28" x2="39" y2="44" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    <polyline points="32,38 39,45 46,38" fill="none" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" />
    <line x1="30" y1="50" x2="48" y2="50" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
  </Wrap>
);

/* Fingerspelling player - play triangle wrapped in a hand-like ring. */
export const IllusPlayer = (props) => (
  <Wrap {...props}>
    {/* big circle */}
    <circle cx="32" cy="32" r="22" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    {/* inner halo */}
    <circle cx="32" cy="32" r="16" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeDasharray="2 3" />
    {/* play triangle */}
    <path d="M28 23 L42 32 L28 41 Z" fill="currentColor" stroke="currentColor" strokeWidth={STROKE_W * 0.6} strokeLinejoin="round" />
  </Wrap>
);

/* Signspell - lightning bolt over a calendar tile (today's game). */
export const IllusSignspell = (props) => (
  <Wrap {...props}>
    {/* calendar body */}
    <rect x="10" y="14" width="44" height="40" rx="4" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* top binder */}
    <rect x="10" y="14" width="44" height="9" rx="2" fill="currentColor" />
    {/* binder rings */}
    <line x1="20" y1="8" x2="20" y2="18" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    <line x1="44" y1="8" x2="44" y2="18" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* lightning bolt */}
    <path d="M34 26 L24 40 H32 L28 50 L42 33 H34 L38 26 Z" fill="currentColor" stroke="currentColor" strokeWidth={STROKE_W * 0.5} strokeLinejoin="round" />
  </Wrap>
);

/* BSL Sign Quiz - speech bubble framing a question mark. */
export const IllusSignQuiz = (props) => (
  <Wrap {...props}>
    {/* speech bubble */}
    <path
      d="M10 14 H54 a3 3 0 0 1 3 3 V41 a3 3 0 0 1 -3 3 H30 L20 54 V44 H10 a3 3 0 0 1 -3 -3 V17 a3 3 0 0 1 3 -3 Z"
      fill="#FFFFFF"
      stroke="currentColor"
      strokeWidth={STROKE_W}
      strokeLinejoin="round"
    />
    {/* question mark */}
    <path
      d="M27 23.5 a5 5 0 0 1 10 0 c0 3 -2 4 -3.5 5 c-1.2 0.8 -1.5 1.5 -1.5 3 V33"
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE_W * 1.2}
      strokeLinecap="round"
    />
    <circle cx="32" cy="38" r="2" fill="currentColor" />
  </Wrap>
);

/* Fingerspelling quiz - a small handshape + question mark. */
export const IllusFingerspellQuiz = (props) => (
  <Wrap {...props}>
    {/* hand silhouette - simplified BSL fingerspelling B-handshape */}
    <path
      d="M16 50 V32 c0 -2 1.5 -3 3 -3 s3 1 3 3 V18 c0 -2 1.5 -3 3 -3 s3 1 3 3 V32 V18 c0 -2 1.5 -3 3 -3 s3 1 3 3 V32 V20 c0 -2 1.5 -3 3 -3 s3 1 3 3 V36 c0 8 -3 14 -10 14 H22 c-3 0 -6 -2 -6 -6 Z"
      fill={PAPER}
      stroke="currentColor"
      strokeWidth={STROKE_W * 0.9}
      strokeLinejoin="round"
    />
    {/* question mark above */}
    <circle cx="48" cy="20" r="9" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    <path
      d="M45 17 a3 3 0 0 1 6 0 c0 2 -2 2.5 -2.5 3.5 V22"
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE_W}
      strokeLinecap="round"
    />
    <circle cx="48" cy="25" r="1.4" fill="currentColor" />
  </Wrap>
);

/* Quiz leaderboard - podium with first place. */
export const IllusLeaderboard = (props) => (
  <Wrap {...props}>
    {/* trophy / star on top */}
    <path d="M32 6 L34.6 12 L41 13 L36 17.5 L37 24 L32 21 L27 24 L28 17.5 L23 13 L29.4 12 Z" fill="currentColor" stroke="currentColor" strokeWidth={STROKE_W * 0.5} strokeLinejoin="round" />
    {/* podium - first place (centre, tallest) */}
    <rect x="24" y="30" width="16" height="26" rx="1.5" fill="currentColor" />
    {/* second place left */}
    <rect x="8" y="38" width="16" height="18" rx="1.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* third place right */}
    <rect x="40" y="44" width="16" height="12" rx="1.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* '1' label */}
    <text x="32" y="48" textAnchor="middle" fontFamily="Georgia, serif" fontSize="13" fontWeight="700" fill="#FFFFFF">1</text>
  </Wrap>
);

/* Lesson plan - clipboard with bullet checks. */
export const IllusLesson = (props) => (
  <Wrap {...props}>
    {/* clipboard back */}
    <rect x="12" y="14" width="40" height="44" rx="3" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* clip at top */}
    <rect x="24" y="8" width="16" height="10" rx="2" fill="currentColor" />
    {/* check rows */}
    <path d="M20 28 L23 31 L28 26" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <line x1="32" y1="29" x2="46" y2="29" stroke="currentColor" strokeWidth={STROKE_W * 0.85} strokeLinecap="round" />
    <path d="M20 38 L23 41 L28 36" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <line x1="32" y1="39" x2="44" y2="39" stroke="currentColor" strokeWidth={STROKE_W * 0.85} strokeLinecap="round" />
    <path d="M20 48 L23 51 L28 46" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" fill="none" />
    <line x1="32" y1="49" x2="46" y2="49" stroke="currentColor" strokeWidth={STROKE_W * 0.85} strokeLinecap="round" />
  </Wrap>
);

/* Audience / "For" pages - three abstract people heads. */
export const IllusAudience = (props) => (
  <Wrap {...props}>
    {/* centre person */}
    <circle cx="32" cy="22" r="7" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <path d="M20 50 c0 -8 6 -12 12 -12 s12 4 12 12 v6 H20 Z" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    {/* left person */}
    <circle cx="14" cy="26" r="5.5" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    <path d="M5 52 c0 -7 4 -10 9 -10 v14 H5 Z" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    {/* right person */}
    <circle cx="50" cy="26" r="5.5" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    <path d="M50 42 c5 0 9 3 9 10 v4 H50 Z" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
  </Wrap>
);

/* Course / Enrol - graduation cap. */
export const IllusCourse = (props) => (
  <Wrap {...props}>
    {/* cap top */}
    <path d="M4 26 L32 14 L60 26 L32 38 Z" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    {/* cap underside */}
    <path d="M16 32 V44 c0 4 7 7 16 7 s16 -3 16 -7 V32" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    {/* tassel */}
    <line x1="58" y1="27" x2="58" y2="40" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    <circle cx="58" cy="43" r="2.5" fill="currentColor" />
  </Wrap>
);

/* Dictionary - open book. */
export const IllusDictionary = (props) => (
  <Wrap {...props}>
    {/* spine */}
    <line x1="32" y1="14" x2="32" y2="54" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* left page */}
    <path d="M32 14 C24 12 14 12 8 14 V52 C14 50 24 50 32 52 Z" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    {/* right page */}
    <path d="M32 14 C40 12 50 12 56 14 V52 C50 50 40 50 32 52 Z" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    {/* lines left */}
    <line x1="14" y1="22" x2="28" y2="22" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
    <line x1="14" y1="28" x2="28" y2="28" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
    <line x1="14" y1="34" x2="26" y2="34" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
    {/* lines right */}
    <line x1="36" y1="22" x2="50" y2="22" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
    <line x1="36" y1="28" x2="50" y2="28" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
    <line x1="36" y1="34" x2="48" y2="34" stroke="currentColor" strokeWidth={STROKE_W * 0.7} strokeLinecap="round" />
  </Wrap>
);

/* Home - house with a chimney. */
export const IllusHome = (props) => (
  <Wrap {...props}>
    <path d="M8 32 L32 12 L56 32 V54 a2 2 0 0 1 -2 2 H10 a2 2 0 0 1 -2 -2 Z" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    <rect x="26" y="36" width="12" height="20" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    {/* chimney */}
    <rect x="44" y="18" width="6" height="10" fill="currentColor" />
  </Wrap>
);

/* Contact - speech bubble with three dots. */
export const IllusContact = (props) => (
  <Wrap {...props}>
    <path d="M8 16 H56 a3 3 0 0 1 3 3 V42 a3 3 0 0 1 -3 3 H28 L18 55 V45 H8 a3 3 0 0 1 -3 -3 V19 a3 3 0 0 1 3 -3 Z" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    <circle cx="22" cy="30" r="2.5" fill="currentColor" />
    <circle cx="32" cy="30" r="2.5" fill="currentColor" />
    <circle cx="42" cy="30" r="2.5" fill="currentColor" />
  </Wrap>
);

/* Larger "hero" illustration used in the featured tile. Composes
   the Sign Quiz speech bubble + a hand to telegraph: see a sign,
   pick the meaning. */
export const IllusFeatured = (props) => (
  <Wrap viewBox="0 0 96 64" {...props}>
    {/* big speech bubble on the left */}
    <path
      d="M6 12 H50 a3 3 0 0 1 3 3 V40 a3 3 0 0 1 -3 3 H25 L16 52 V43 H6 a3 3 0 0 1 -3 -3 V15 a3 3 0 0 1 3 -3 Z"
      fill="rgba(255,255,255,0.16)"
      stroke="#FFFFFF"
      strokeWidth={STROKE_W * 0.9}
      strokeLinejoin="round"
    />
    {/* question mark */}
    <path
      d="M22 22 a6 6 0 0 1 12 0 c0 3.5 -2.5 4.5 -4 6 c-1.2 1 -1.5 2 -1.5 3 V32"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth={STROKE_W}
      strokeLinecap="round"
    />
    <circle cx="28" cy="37" r="2.1" fill="#FFFFFF" />
    {/* hand thumbs-up / point on the right */}
    <path
      d="M62 50 V36 c0 -2 1.5 -3 3 -3 s3 1 3 3 V20 c0 -2 1.5 -3 3 -3 s3 1 3 3 V32 V20 c0 -2 1.5 -3 3 -3 s3 1 3 3 V32 V22 c0 -2 1.5 -3 3 -3 s3 1 3 3 V36 c0 8 -3 14 -10 14 H68 c-3 0 -6 -2 -6 -6 Z"
      fill="rgba(255,255,255,0.16)"
      stroke="#FFFFFF"
      strokeWidth={STROKE_W}
      strokeLinejoin="round"
    />
  </Wrap>
);

/* Embed widgets - a browser window with </> code brackets. */
export const IllusCode = (props) => (
  <Wrap {...props}>
    {/* browser frame */}
    <rect x="6" y="12" width="52" height="40" rx="3.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* title bar */}
    <line x1="6" y1="22" x2="58" y2="22" stroke="currentColor" strokeWidth={STROKE_W} />
    {/* traffic dots */}
    <circle cx="11" cy="17" r="1.3" fill="currentColor" />
    <circle cx="15.5" cy="17" r="1.3" fill="currentColor" />
    <circle cx="20" cy="17" r="1.3" fill="currentColor" />
    {/* < bracket */}
    <path d="M22 40 L17 33 L22 26" fill="none" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" />
    {/* slash */}
    <line x1="29" y1="42" x2="35" y2="24" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* > bracket */}
    <path d="M42 26 L47 33 L42 40" fill="none" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" />
  </Wrap>
);

/* Greetings card maker - a folded card with a tiny BSL hand-sign
   silhouette on the cover plus a heart sparkle, communicating
   "boutique BSL greetings card" at a glance. */
export const IllusCard = (props) => (
  <Wrap {...props}>
    {/* back panel of folded card */}
    <rect x="11" y="14" width="42" height="36" rx="2.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* fold crease */}
    <line x1="32" y1="14" x2="32" y2="50" stroke="currentColor" strokeWidth={STROKE_W * 0.65} strokeDasharray="2 2" />
    {/* front cover lifted slightly */}
    <path d="M14 11 H32 V47 H14 a1.5 1.5 0 0 1 -1.5 -1.5 V12.5 A1.5 1.5 0 0 1 14 11 Z" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} strokeLinejoin="round" />
    {/* cover decoration: little body + raised hand silhouette */}
    <circle cx="23" cy="22" r="3.2" fill="currentColor" />
    <path d="M19.5 26 L19.5 35 H26.5 V26" fill="currentColor" />
    <path d="M26 25 L29 19" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* heart sparkle top-right of cover */}
    <path d="M27 14 c0.6 -1 2.2 -1 2.8 0 c0.6 -1 2.2 -1 2.8 0 c0.4 0.8 -0.4 1.7 -1 2.3 L29.4 18.7 L27.2 16.3 c-0.6 -0.6 -1.4 -1.5 -1 -2.3 Z" fill="currentColor" />
    {/* signature line on cover */}
    <line x1="17" y1="40" x2="27" y2="40" stroke="currentColor" strokeWidth={STROKE_W * 0.75} strokeLinecap="round" />
    <line x1="17" y1="43.5" x2="24" y2="43.5" stroke="currentColor" strokeWidth={STROKE_W * 0.75} strokeLinecap="round" />
  </Wrap>
);

/* Daily BSL practice - a 2x2 grid of activity tiles (the daily hub). */
export const IllusDaily = (props) => (
  <Wrap {...props}>
    <rect x="11" y="11" width="18" height="18" rx="3" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <rect x="35" y="11" width="18" height="18" rx="3" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <rect x="11" y="35" width="18" height="18" rx="3" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <rect x="35" y="35" width="18" height="18" rx="3" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <circle cx="20" cy="20" r="3" fill="currentColor" />
    <path d="M40 20 h8 M44 16 v8" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    <path d="M16 44 l3 3 l5 -6" fill="none" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" />
    <path d="M40 44 h8" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
  </Wrap>
);

/* Sign Memory - two cards, one face-down, one face-up (a matched pair). */
export const IllusMemory = (props) => (
  <Wrap {...props}>
    <rect x="10" y="17" width="25" height="33" rx="4" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <circle cx="22.5" cy="33.5" r="5" fill="none" stroke="currentColor" strokeWidth={STROKE_W} />
    <rect x="31" y="13" width="25" height="33" rx="4" fill="#FFFFFF" stroke="currentColor" strokeWidth={STROKE_W} />
    <path d="M38 30 l4.5 4.5 l7 -9" fill="none" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" strokeLinejoin="round" />
  </Wrap>
);

/* Fingerspell Five - a row of letter tiles: one solved, the rest still to find. */
export const IllusWordGuess = (props) => (
  <Wrap {...props}>
    <rect x="11" y="25" width="12" height="14" rx="2.5" fill="currentColor" />
    <text x="17" y="35.5" textAnchor="middle" fontFamily="Georgia, serif" fontSize="10" fontWeight="700" fill="#FFFFFF">B</text>
    <rect x="26" y="25" width="12" height="14" rx="2.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <line x1="29" y1="36" x2="35" y2="36" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    <rect x="41" y="25" width="12" height="14" rx="2.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    <line x1="44" y1="36" x2="50" y2="36" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
  </Wrap>
);

/* Fingerspelling Invaders - an arcade invader descending toward a base. */
export const IllusInvaders = (props) => (
  <Wrap {...props}>
    {/* invader body */}
    <path
      d="M20 22 h24 a4 4 0 0 1 4 4 v9 a4 4 0 0 1 -4 4 h-3 l-3 4 -3 -4 h-6 l-3 4 -3 -4 h-3 a4 4 0 0 1 -4 -4 v-9 a4 4 0 0 1 4 -4 Z"
      fill={PAPER}
      stroke="currentColor"
      strokeWidth={STROKE_W}
      strokeLinejoin="round"
    />
    {/* antennae */}
    <path d="M25 22 l-3 -6 M39 22 l3 -6" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* eyes */}
    <circle cx="27" cy="30" r="2.4" fill="currentColor" />
    <circle cx="37" cy="30" r="2.4" fill="currentColor" />
    {/* base line + cannon it's falling toward */}
    <path d="M12 54 H52" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    <rect x="30" y="49" width="4" height="5" rx="1" fill="currentColor" />
  </Wrap>
);

/* Fingerspell Maze - a Pac-Man-style board eating a trail of pellets. */
export const IllusMaze = (props) => (
  <Wrap {...props}>
    {/* board */}
    <rect x="10" y="10" width="44" height="44" rx="7" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* a few maze walls */}
    <path d="M19 20 h13" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    <path d="M42 18 v11" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    <path d="M19 29 h8" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* pellet trail the muncher is heading for */}
    <circle cx="35" cy="41" r="1.8" fill="currentColor" opacity="0.55" />
    <circle cx="41" cy="41" r="1.8" fill="currentColor" opacity="0.5" />
    <circle cx="47" cy="41" r="1.8" fill="currentColor" opacity="0.35" />
    {/* muncher, mouth open toward the pellets */}
    <circle cx="23" cy="41" r="6.5" fill="currentColor" />
    <path d="M23 41 L30 37.5 L30 44.5 Z" fill={PAPER} />
  </Wrap>
);

/* Family signs - a printable chart poster with a little family on it. */
export const IllusFamilyChart = (props) => (
  <Wrap {...props}>
    {/* poster */}
    <rect x="12" y="10" width="40" height="44" rx="3.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* title underline */}
    <line x1="18" y1="18" x2="33" y2="18" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* two adults + a child */}
    <circle cx="21" cy="29" r="3" fill="currentColor" />
    <rect x="17" y="34" width="8" height="11" rx="3" fill="currentColor" />
    <circle cx="31" cy="29" r="3" fill="currentColor" />
    <rect x="27" y="34" width="8" height="11" rx="3" fill="currentColor" />
    <circle cx="41" cy="33" r="2.4" fill="currentColor" />
    <rect x="37.6" y="37" width="6.8" height="8" rx="2.6" fill="currentColor" />
  </Wrap>
);

/* Greetings signs - a printable chart poster with a waving hand. */
export const IllusGreetingsChart = (props) => (
  <Wrap {...props}>
    {/* poster */}
    <rect x="12" y="10" width="40" height="44" rx="3.5" fill={PAPER} stroke="currentColor" strokeWidth={STROKE_W} />
    {/* title underline */}
    <line x1="18" y1="18" x2="33" y2="18" stroke="currentColor" strokeWidth={STROKE_W} strokeLinecap="round" />
    {/* waving hand: four fingers, a thumb and a palm */}
    <g fill="currentColor">
      <rect x="26.5" y="26" width="2.6" height="12" rx="1.3" />
      <rect x="30" y="24" width="2.6" height="14" rx="1.3" />
      <rect x="33.5" y="25" width="2.6" height="13" rx="1.3" />
      <rect x="37" y="27" width="2.6" height="11" rx="1.3" />
      <rect x="25" y="34" width="16" height="10" rx="4" />
      <rect x="22.5" y="34" width="4.5" height="6" rx="2.2" transform="rotate(-32 24.7 37)" />
    </g>
    {/* motion arcs - the wave */}
    <path d="M44 28 q3 3.5 0 7" fill="none" stroke="currentColor" strokeWidth={STROKE_W * 0.8} strokeLinecap="round" />
    <path d="M47.5 25 q4.5 5 0 12" fill="none" stroke="currentColor" strokeWidth={STROKE_W * 0.8} strokeLinecap="round" opacity="0.6" />
  </Wrap>
);

export const MENU_ILLUSTRATIONS = {
  vocab: IllusVocab,
  invaders: IllusInvaders,
  maze: IllusMaze,
  family: IllusFamilyChart,
  greetings: IllusGreetingsChart,
  daily: IllusDaily,
  memory: IllusMemory,
  wordguess: IllusWordGuess,
  printer: IllusPrinter,
  wordsearch: IllusWordSearch,
  hub: IllusHub,
  code: IllusCode,
  card: IllusCard,
  player: IllusPlayer,
  signspell: IllusSignspell,
  signquiz: IllusSignQuiz,
  fingerspellquiz: IllusFingerspellQuiz,
  leaderboard: IllusLeaderboard,
  featured: IllusFeatured,
  lesson: IllusLesson,
  audience: IllusAudience,
  course: IllusCourse,
  dictionary: IllusDictionary,
  home: IllusHome,
  contact: IllusContact,
};
